import React from 'react'
import { useHistory } from 'react-router-dom'

// Components
import { Button, ErrorMessage } from '@sketch/components'
import Footer from 'components/Footer'
import { CenterContent } from 'containers/CenterContent'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import EmptyHeader from 'containers/EmptyHeader'
import PageLayout from 'layouts/PageLayout'
import GenericErrorView from 'views/GenericErrorView'

// Hooks
import { useSignInToUnauthorizedWorkspace } from 'modules/sso/operations'
import useUserProfile from 'modules/user/useUserProfile'
import { useGetWorkspacesQuery } from '@sketch/gql-types'

import { routes } from '@sketch/constants'
import { ReactComponent as LockIcon } from '@sketch/icons/lock-64-svg'

interface WrongAuthorizationViewProps {
  workspaceIdentifier?: string
  email?: string
}

const Description = ({ hasWorkspace }: { hasWorkspace: boolean }) => {
  return hasWorkspace ? (
    <>
      This document is part of another workspace that you are already a member
      of. <br />
      Please sign in to view this document
    </>
  ) : (
    <>
      To access this document, you need to log into the account that has access
      to it.
    </>
  )
}

export const WrongAuthorizationView = (props: WrongAuthorizationViewProps) => {
  const { workspaceIdentifier, email } = props
  /**
   * Skipping this query will make all data/error and loading values to be
   * undefined/false.
   */
  const { data, loading, error } = useGetWorkspacesQuery({
    skip: !workspaceIdentifier,
  })

  const { signIn, signInToPersonalAccount } = useSignInToUnauthorizedWorkspace()
  const { data: userData } = useUserProfile()
  const history = useHistory()

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error) {
    return <GenericErrorView />
  }

  const workspace = data?.me?.workspaces?.find(
    workspace => workspace?.identifier === workspaceIdentifier
  )

  const user = {
    name: userData?.me?.name ?? '',
    avatarSrc: userData?.me?.avatar?.small ?? '',
    email: email || '',
  }

  return (
    <PageLayout header={<EmptyHeader />} footer={<Footer />}>
      <CenterContent>
        <ErrorMessage.Generic
          title="Sign in to view this document"
          icon={<LockIcon />}
          iconSize="large"
          extra={
            <Button
              variant="primary"
              onClick={() => {
                if (workspace) {
                  signIn({
                    workspace: {
                      workspaceId: workspace.identifier,
                      workspaceName: workspace.name,
                      ssoEnabled: workspace.customer?.ssoEnabled || false,
                      ssoStartUrl: workspace.customer?.ssoStartUrl || undefined,
                    },
                    user,
                    redirectBackToCurrentUrl: true,
                  })
                } else {
                  signInToPersonalAccount({
                    workspaceName: 'Personal',
                    user,
                    onCompleted: () => {
                      const currentLocation = history.location

                      history.push(routes.ENTRY.create({}))
                      history.replace(currentLocation)
                    },
                  })
                }
              }}
            >
              Sign In
            </Button>
          }
        >
          <Description hasWorkspace={true} />
        </ErrorMessage.Generic>
      </CenterContent>
    </PageLayout>
  )
}
